import {
  ContactFormItem,
  ContactSection as ContactSectionUI,
} from "@repo/ui-parkmobile/sections/ContactSection";
import type {
  Button,
  ContactSection,
  InputField,
  InputGroup,
  TextHeader,
} from "@/generated/sb";
import {
  // assetResolver, // Commented out for US/Canada only (+1) support
  buttonResolver,
  contactFormFieldsResolver,
  imageStoryblokResolver,
  sectionTitleResolver,
} from "@/lib/storyblok/adaptors";
import { trackFormSubmit, trackFormStart, trackFormInputClick, trackFormError, trackTermsAgreement } from "@/utils/trackingScripts";
import { addUtmIfOutbound } from "@repo/ui-parkmobile/utils/url";
// import { useGlobalDataContext } from "@/components/GlobalDataContext";
// import { getCountryFromCountryCode } from "country-codes-flags-phone-codes";
import { ThanksSection } from "@/components/sections/ThanksSection";
import {
  StoryblokServerRichText,
  StoryblokRichTextNode,
  storyblokEditable,
} from "@storyblok/react/rsc";
import { JSXElementConstructor, ReactElement, useMemo } from "react";
import { STATE_PROVINCE_OPTIONS } from "@/constants/stateProvinceOptions";

export type ContactSectionProps = {
  blok: ContactSection & { embedded?: boolean; lastTextHeader?: TextHeader; sectionName?: string; redirectURL?: string };
};

export function ContactSection({ blok, ...props }: ContactSectionProps) {
  // Commented out for US/Canada only (+1) support - can be restored later if needed
  // const { globalPhoneCountryCodes } = useGlobalDataContext();

  const headProps = useMemo(() => sectionTitleResolver(blok.head), [blok.head]);

  const formId = typeof blok.form !== "string" ? (blok.form?.id?.toString() || blok.form?._uid) : undefined;
  const formUuid = typeof blok.form !== "string" ? blok.form?.uuid : undefined;

  const ownHeadTitle = blok.head?.[0]?.title?.[0]?.content;
  const lastTextHeaderTitle = blok.lastTextHeader?.content?.[0]?.title?.[0]?.content;
  const sectionName = blok.sectionName || ownHeadTitle || lastTextHeaderTitle || "Contact Form";

  // Commented out for US/Canada only (+1) support - can be restored later if needed
  // const countryCodeOptions = useMemo(() => {
  //   const options = globalPhoneCountryCodes?.map((country) => {
  //     const countryData = getCountryFromCountryCode(
  //       String(country.countryCode).toUpperCase(),
  //     );
  //
  //     const dialCode = countryData?.dialCode;
  //
  //     return {
  //       label: `${countryData?.name || country.countryName} || ${dialCode}`,
  //       value: dialCode,
  //       flag: country.flag ? assetResolver(country.flag)?.src : undefined,
  //       numberLength: Number(country.numberLength),
  //       validationMessage: country.validationMessage,
  //     };
  //   });
  //
  //   return options;
  // }, [globalPhoneCountryCodes]);

  if (!blok.form || typeof blok?.form === "string") {
    return null;
  }

  const formFields = blok.form?.content.fields;
  const formButton = blok.form?.content.button;
  const successScreen = blok.form?.content.successScreen?.[0];
  const subject = blok.form?.content.subject;
  const endpoint = blok.form?.content.endpoint;
  const hiddenElements = blok?.form?.content?.hiddenElements?.reduce(
    (acc, current) => {
      if (current.title && current.value) {
        acc[current.title as string] = current.value;
      }
      return acc;
    },
    {} as Record<string, unknown>,
  );

  const mappedFields = formFields?.map((field) => {
    // Handle inputGroup
    if (field.component === "inputGroup") {
      const mappedGroup = {
        ...field,
        items: field.items?.map((item) => {
          const mappedItem = {
            ...item,
            required: item.required || false,
            requiredErrorText: item.requiredErrorText,
            validations:
              item.validations?.map((validation) => ({
                regularExpression: validation.regularExpression,
                errorMessage: validation.errorMessage,
              })) || [],
          };

          // Commented out for US/Canada only (+1) support - can be restored later if needed
          // if (item.type === "tel") {
          //   const telField = {
          //     ...mappedItem,
          //     options: countryCodeOptions,
          //   };
          //   return telField;
          // }

          // Add state/province options for select fields with name "stateProvinceDropdown"
          if (item.type === "select" && item.name === "stateProvinceDropdown") {
            const selectField = {
              ...mappedItem,
              options: STATE_PROVINCE_OPTIONS,
            };
            return selectField;
          }

          return mappedItem;
        }),
      };
      return mappedGroup;
    }

    const mappedField = {
      ...field,
      required: field.required || false,
      requiredErrorText: field.requiredErrorText,
      validations:
        field.validations?.map((validation) => ({
          regularExpression: validation.regularExpression,
          errorMessage: validation.errorMessage,
        })) || [],
    };

    // Commented out for US/Canada only (+1) support - can be restored later if needed
    // if (field.type === "tel") {
    //   const telField = {
    //     ...mappedField,
    //     options: countryCodeOptions,
    //   };
    //   return telField;
    // }

    // Add state/province options for select fields with name "stateProvinceDropdown"
    if (field.type === "select" && field.name === "stateProvinceDropdown") {
      const selectField = {
        ...mappedField,
        options: STATE_PROVINCE_OPTIONS,
      };
      return selectField;
    }

    return mappedField;
  });

  const handleFormSubmit = async (
    formData: Record<string, string | number>,
  ): Promise<void | { success: boolean }> => {
    try {
      await fetch("/api/submit-form", {
        method: "POST",
        body: JSON.stringify({
          ...formData,
          ...hiddenElements,
          subject,
          endpoint,
          formUuid,
        }),
        headers: {
          "email-subject": subject,
          "email-recipient": endpoint,
        },
      });

      // Redirect after successful submission if redirectURL is provided
      if (blok.redirectURL) {
        window.location.href = addUtmIfOutbound(blok.redirectURL);
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  return (
    <ContactSectionUI
      {...props}
      {...storyblokEditable(blok)}
      mode={blok.mode}
      head={headProps}
      image={imageStoryblokResolver(blok?.image?.[0])}
      backgroundColor={blok.backgroundColor || "none"}
      topPadding={blok.topPadding || "none"}
      bottomPadding={blok.bottomPadding || "none"}
      embedded={blok.embedded || false}
      inputBackgroundColor={blok.inputBackgroundColor}
      formBackgroundColor={blok.formBackgroundColor}
      id={blok.anchor}
      form={{
        ...blok.form?.content,
        fields: contactFormFieldsResolver(
          mappedFields as (InputField | InputGroup)[],
        ) as ContactFormItem[],
        // The form's submit control can never be a Navattic trigger; force the
        // regular type so a mis-set Type in Storyblok cannot remove the submit button.
        button: formButton?.[0]
          ? buttonResolver({ ...formButton[0], type: "regular" } as Button)
          : null,
        privacyPolicyText: blok?.form?.content?.privacyPolicyText ? (
          <StoryblokServerRichText
            doc={
              blok.form.content.privacyPolicyText as StoryblokRichTextNode<
                ReactElement<unknown, string | JSXElementConstructor<unknown>>
              >
            }
          />
        ) : null,
      }}
      trackFormStart={() =>
        trackFormStart({
          section: sectionName,
          config: "contactForm",
          formName: sectionName,
        })
      }
      trackFormInputClick={(fieldName?: string) =>
        trackFormInputClick({
          section: sectionName,
          config: "contactForm",
          formName: sectionName,
          fieldName,
        })
      }
      trackFormSubmit={({
        formValue,
        firstName,
        lastName,
        email,
        jobTitle,
        countryCode,
        stateProvinceDropdown,
        contactMessage,
        ...rest
      }: {
        formValue: number;
        firstName?: string;
        lastName?: string;
        email?: string;
        jobTitle?: string;
        countryCode?: string;
        stateProvinceDropdown?: string;
        contactMessage?: string;
        [key: string]: string | number | undefined;
      }) =>
        trackFormSubmit({
          section: sectionName,
          formName: sectionName,
          formValue,
          config: "contactForm",
          form_id: formId,
          status: "submitted",
          // firstName,
          // lastName,
          // email,
          industry: jobTitle,
          country: countryCode,
          state: stateProvinceDropdown,
          contactReason: contactMessage,
          ...rest,
        })
      }
      trackFormError={(errorType: string, errorMessage: string, fieldName?: string) =>
        trackFormError({
          section: sectionName,
          config: "contactForm",
          formName: sectionName,
          errorType: errorType as "missing_field" | "validation_error" | "captcha_error" | "checkbox_error" | "submission_error",
          errorMessage,
          fieldName,
        })
      }
      onTermsAgreementChange={(agreement: boolean) =>
        trackTermsAgreement({
          section: sectionName,
          config: "contactSection",
          agreement,
        })
      }
      successScreen={
        successScreen
          ? (props: { onButtonClick: () => void }) => (
              <ThanksSection blok={successScreen} {...props} />
            )
          : null
      }
      onSubmit={
        handleFormSubmit as ((
          formData: Record<string, string | number>,
        ) => Promise<void | { success: boolean }>) &
          React.FormEventHandler<HTMLElement>
      }
    />
  );
}
